// @flow

import React, { useState } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@riseart/antd-provider';
import { Heading, Paragraph } from '@riseart/common';
import { CloseIcon } from '@riseart/icons';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { EnquireForm } from 'shared_hocs/forms/Enquire';
import { EnquireBaseFormModel } from 'shared_models/forms/EnquireBase';
import { TermsSection } from 'shared_components/forms/common/TermsSection';
import { CreateMailingListMutation } from 'shared_data/providers/queries/CreateMailingListMutation';
import { cmsEnquireRequestSend } from 'shared_services/redux/actions/application/enquire';
import { formModels } from 'shared_components/cms/modules/forms/formModels';
import { subscribeFragment } from 'shared_models/forms/fragments/subscribe';

import { cmsFormWrapperCls } from 'shared_components/cms/modules/forms/Form.less';

type postActionType = {
  type: string,
  props: Object,
};

type Props = {
  type: string,
  title: string,
  description: string,
  buttonText: string,
  postAction: postActionType,
  customFields?: Array<Object>,
  allowSubscription?: boolean,
  subscribe?: boolean,
};

/**
 * postActionHandler
 *
 * @param {postActionType} params
 * @returns {void}
 */
function postActionHandler({ type, props }: postActionType): void {
  switch (type) {
    case 'openTab': {
      if (props && props.href) {
        LocationManager.redirect(props.href, { target: props.href });
      }

      break;
    }
    default:
      break;
  }
}

/**
 * CmsModuleEnquireForm
 *
 * @param {Props} props
 * @returns {Node}
 */
export const CmsModuleEnquireForm = ({
  type,
  title,
  description,
  buttonText,
  postAction,
  allowSubscription,
  subscribe,
}: Props): Node => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [formRef, setFormRef] = useState(null);
  const onSubmit = (data, props, formRef) => {
    setIsSubmit(true);
    if (formRef) {
      setFormRef(formRef);
    }

    if (postAction) {
      postActionHandler(postAction);
    }
  };

  const onCancel = () => {
    if (formRef) {
      formRef.resetFields();
    }

    setIsSubmit(false);
  };

  const FORM_SETTINGS = { ...(formModels[type] || EnquireBaseFormModel) };

  // If subscribe flag is set - subscribe automatically without showing subscribe input
  if (subscribe === true) {
    FORM_SETTINGS.fields = [
      ...FORM_SETTINGS.fields,
      ...(subscribe
        ? [
            {
              tag: 'input',
              type: 'hidden',
              name: 'subscribe',
            },
          ]
        : []),
    ];
  } else if (allowSubscription !== false) {
    FORM_SETTINGS.fields = [...FORM_SETTINGS.fields, ...subscribeFragment];
  }

  return (
    <div className={cmsFormWrapperCls}>
      {/* eslint-disable-next-line */}
      <a name={LocalizedConfig.get('navigation.uri.hash.params.enquireForm')} />
      <section>
        {title ? (
          <Heading tag="h2" align="center">
            {title}
          </Heading>
        ) : null}
        {description ? <Paragraph align="center">{description}</Paragraph> : null}
        <CreateMailingListMutation>
          {(createMailingList) => (
            <EnquireForm
              actionSubmit={cmsEnquireRequestSend}
              onSubmit={onSubmit}
              submitActionLabel={buttonText}
              formSettings={FORM_SETTINGS}
              createMailingList={createMailingList}
              skipInitialValues={!!formRef}
              additionalInitialValues={{ subscribe: !!subscribe }}
              trackedData={{ trigger: type }}
            />
          )}
        </CreateMailingListMutation>
        <TermsSection translationValues={{ actionLabel: buttonText }} />
      </section>
      {isSubmit ? (
        <Modal
          title={<FormattedMessage id="forms.cmsEnquire.successTitle" />}
          open={isSubmit}
          closeIcon={<CloseIcon />}
          onCancel={onCancel}
          footer={null}
        >
          <FormattedMessage id="forms.cmsEnquire.success" />
        </Modal>
      ) : null}
    </div>
  );
};
